import { useCallback } from 'react';
import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import { AccessStatus, type AdministrationGroup, type AdministrationUser } from '@/modules/api/codegen';
import { useConfirmationModal } from '@/modules/modals';
import { Button, Text } from '@/modules/ui';

import { UserAdministrationType } from '../../../constants';
import { useUpdateGroups, useUpdateUsers } from '../../../hooks';
import * as felaRules from './BatchActions.rules';

export type BatchActionsProps =
    | {
          type: UserAdministrationType.Users;
          records: AdministrationUser[];
      }
    | {
          type: UserAdministrationType.Groups;
          records: AdministrationGroup[];
      };

export const BatchActions = ({ type, records }: BatchActionsProps) => {
    const { css } = useFela();
    const { formatMessage } = useIntl();
    const { openConfirmationModal } = useConfirmationModal();

    const allRecordsHaveActiveStatus = records.every(record => record.status === AccessStatus.active);
    const allRecordsHaveInactiveStatus = records.every(record => record.status === AccessStatus.inactive);

    const updateUsersMutation = useUpdateUsers();
    const updateGroupsMutation = useUpdateGroups();

    const handleChangeStatus = useCallback(
        (status: AccessStatus) => {
            if (type === UserAdministrationType.Users) {
                const requestUserStatusChange = () =>
                    updateUsersMutation.mutateAsync({
                        users: records,
                        status,
                    });

                if (status === AccessStatus.active) {
                    openConfirmationModal({
                        title: (
                            <FormattedMessage
                                id={`user-administration.table.batchActions.${type}.grantAccess.confirmation.title`}
                                values={{ numberOfRecords: records.length }}
                            />
                        ),
                        content: <FormattedMessage id={`user-administration.table.actions.grantAccess.confirmation.content`} />,
                        onConfirm: async () => {
                            await requestUserStatusChange();
                        },
                        confirmButtonText: formatMessage({
                            id: `user-administration.table.actions.grantAccess.confirmation.confirm`,
                        }),
                        cancelButtonText: formatMessage({
                            id: `user-administration.table.actions.grantAccess.confirmation.cancel`,
                        }),
                    });

                    return;
                }

                requestUserStatusChange();
            }

            if (type === UserAdministrationType.Groups) {
                updateGroupsMutation.mutate({
                    data: {
                        groupIds: records.map(record => record.id!),
                        status,
                    },
                });
            }
        },
        [type, records, updateUsersMutation, updateGroupsMutation, openConfirmationModal, formatMessage],
    );

    if (allRecordsHaveInactiveStatus) {
        return (
            <Button
                type='text'
                size='large'
                color='accentPrimary'
                onClick={() => handleChangeStatus(AccessStatus.active)}
                customStyle={felaRules.text}
            >
                <FormattedMessage
                    id={`user-administration.table.batchActions.${type}.grantAccess`}
                    values={{ numberOfRecords: records.length }}
                />
            </Button>
        );
    }

    if (allRecordsHaveActiveStatus) {
        return (
            <Button
                type='text'
                size='large'
                color='inputsError'
                onClick={() => handleChangeStatus(AccessStatus.inactive)}
                customStyle={felaRules.text}
            >
                <FormattedMessage
                    id={`user-administration.table.batchActions.${type}.revokeAccess`}
                    values={{ numberOfRecords: records.length }}
                />
            </Button>
        );
    }

    return (
        <div className={css(felaRules.container)}>
            <Text variant='ParagraphNormalRegular' color='foregroundsPrimary' customStyle={felaRules.text}>
                <FormattedMessage
                    id={`user-administration.table.batchActions.${type}.recordsSelected`}
                    values={{ numberOfRecords: records.length }}
                />
            </Text>
            <Button
                type='text'
                size='large'
                color='accentPrimary'
                onClick={() => handleChangeStatus(AccessStatus.active)}
                customStyle={felaRules.text}
            >
                <FormattedMessage id='user-administration.table.actions.grantAccess' />
            </Button>
            <Button
                type='text'
                size='large'
                color='accentPrimary'
                onClick={() => handleChangeStatus(AccessStatus.inactive)}
                customStyle={felaRules.text}
            >
                <FormattedMessage id='user-administration.table.actions.revokeAccess' />
            </Button>
        </div>
    );
};
